import { Provider } from 'near-api-js/lib/providers';
import { JUMBO_ID } from '../../constants';
import {
  Amm,
  CreateSwapRouteInstructionsParams,
  Quote,
  QuoteParams,
  SwapMode,
  SwapParams,
} from '../common/types';
import { loadAllPools, loadPool } from '../refFinance/fetchers';
import { createRefTransactions } from '../refFinance/instructions';
import { getInputAmount, getOutputAmount } from '../refFinance/math';
import {
  getStableInputAmount,
  getStableOutputAmount,
} from '../refFinance/stable-math';
import { Reserves } from '../refFinance/types';
import { filterMostLiquidUniqPools } from '../refFinance/utils';
import { JumboPool } from './types';

// Jumbo is a fork of RefFinance, that's why we can use loaders from there
class Jumbo implements Amm {
  id: string;
  contractId: string;
  instanceId: number;
  label: 'Jumbo';
  pool: JumboPool;
  isSimplePool: boolean;
  reserves: Reserves;

  constructor(pool: JumboPool) {
    this.label = 'Jumbo';
    this.id = `${pool.id}`;
    this.contractId = JUMBO_ID;
    this.instanceId = pool.id;

    this.pool = pool;
    this.isSimplePool = pool.pool_kind === 'SIMPLE_POOL';
    this.reserves = pool.reserves;
  }

  static async loadPools({
    provider,
  }: {
    provider: Provider;
  }): Promise<Jumbo[]> {
    try {
      const { simplePools, stablePools } = await loadAllPools({
        provider,
        ammId: JUMBO_ID,
      });

      const mostLiquidPools = filterMostLiquidUniqPools(simplePools);

      return [...mostLiquidPools, ...stablePools].map(
        (pool) => new Jumbo(pool)
      );
    } catch (e) {
      console.log('Error loading pools for Ref Finance', e);
    }

    return [];
  }

  getQuote(quoteParams: QuoteParams): Quote {
    const { inputMint, outputMint, amount, slippage, swapMode } = quoteParams;

    const feePct = this.pool.total_fee / 10000;

    if (swapMode === SwapMode.ExactIn) {
      if (this.isSimplePool) {
        const {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
        } = getOutputAmount({
          reserves: this.pool.reserves,
          poolFee: this.pool.total_fee,
          inputMint,
          outputMint,
          inputAmount: amount,
          slippage,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      } else {
        const {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
        } = getStableOutputAmount({
          tokenInId: inputMint,
          tokenOutId: outputMint,
          slippage,
          // @ts-ignore
          stablePool: this.pool,
          amountIn: amount,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      }
    } else if (swapMode === SwapMode.ExactOut) {
      if (this.isSimplePool) {
        const {
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
          notEnoughLiquidity,
        } = getInputAmount({
          reserves: this.pool.reserves,
          poolFee: this.pool.total_fee,
          inputMint,
          outputMint,
          outputAmount: amount,
          slippage,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      } else {
        const {
          notEnoughLiquidity,
          feeAmount,
          amountIn,
          amountOut,
          minAmountOut,
          priceImpact,
        } = getStableInputAmount({
          tokenInId: inputMint,
          tokenOutId: outputMint,
          slippage,
          // @ts-ignore
          stablePool: this.pool,
          amountOut: amount,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      }
    }

    throw new Error('Unknown swap mode type');
  }

  async getPromiseForUpdate({
    provider,
  }: {
    provider: Provider;
  }): Promise<void> {
    return loadPool({
      provider,
      ammId: this.contractId,
      poolId: this.instanceId,
      poolKind: this.pool.pool_kind,
    }).then((pool) => {
      this.pool = pool;
    });
  }

  async createSwapInstructions(params: SwapParams) {
    const { user, swapStep } = params;

    const swapTransactions = await createRefTransactions({
      user,
      swapSteps: [swapStep],
    });

    console.log('swapTransactions', swapTransactions);

    return swapTransactions;
  }

  async createSwapRouteInstructions(params: CreateSwapRouteInstructionsParams) {
    const { user, swapRoute } = params;

    const swapTransactions = await createRefTransactions({
      user,
      swapSteps: swapRoute.steps,
    });

    console.log('swapTransactions', swapTransactions);

    return swapTransactions;
  }

  get reserveTokenMints() {
    return this.pool.token_account_ids;
  }
}

export { Jumbo };
